import type { CtasObjectModel } from '@Types/Shared/Button';
import type { BlockKey } from '@Types/Shared/Options';

import { getBtnSettingsToCSS } from '@Blocks/_lib/btns';

import type {
	ColumnType,
	FactFigureContentClass,
	FactFigureFrontType,
	perRowsType,
} from '@/src/Types/Blocks/FactFigure';
import type { HeadingType } from '@/src/Types/Shared/Heading';
import type { ColumnLink, ColumnUrl } from '@/src/Types/Shared/RepeaterColumn';
import { Button } from '@/src/Views/Shared/Button/Button';
import Heading from '@/src/Views/Shared/Heading/Heading';
import Html from '@Views/Shared/Html';

export default function ColumnClassic(props: FactFigureFrontType) {
	const {
		id,
		titleFormat,
		title,
		perRow,
		columns,
		description,
		containerType,
		headingAlign,
		wrapperType,
		factFigureAlign = 'center',
		factStyle = 'normal',
		headingFontClassName,
		contentFontClass,
		context,
		sectionDecoration,
		ctasStyle,
	} = props;

	const headingProps: HeadingType = {
		title: title,
		description: description,
		titleFormat: titleFormat,
		headingAlign: headingAlign,
		headingFontClassName: headingFontClassName,
		context: context,
	};

	const factFigurePositions = {
		left: 'text-left tablet:mb-3.5 mb-3 block mr-auto',
		center: 'text-center tablet:mb-3.5 mb-3 mx-auto block',
		right: 'text-right tablet:mb-3.5 mb-3 block ml-auto',
	};

	let blockStyle = '';
	if (containerType === 'block') {
		blockStyle =
			'desktop:p-12 tablet:p-5 p-4 bg-minos-factFigure-bg-container-color rounded-minos-factFigure-container border-minos-factFigure-container-border-width border-minos-factFigure-container-border-color';
	}

	let wrapperStyle = '';
	if (wrapperType === 'block') {
		wrapperStyle =
			'desktop:p-6 tablet:p-4 p-3 bg-minos-factFigure-bg-wrapper-color border-minos-factFigure-wrapper-border-color border-minos-factFigure-wrapper-border-width rounded-minos-factFigure-wrapper';
	}

	let padding = '';
	if (sectionDecoration?.isSectionDecorated && sectionDecoration.type === 'wave') {
		padding = 'pt-8 pb-16 tablet:pt-12 tablet:pb-24 desktop:pb-32';
	} else {
		padding = 'py-3.5 tablet:py-6 desktop:py-12';
	}

	return (
		<div id={id} className={`minos-grid relative bg-minos-factFigure-bg-color ${padding}`}>
			<div data-aos='fade-up' className={`relative z-[12] col-start-2 col-end-14 ${blockStyle}`}>
				{/* mobile breakpoints handle in component */}
				<Heading {...headingProps} />

				<div className={`grid grid-cols-4 gap-y-5 tablet:grid-cols-12 tablet:gap-8 ${wrapperStyle}`}>
					{columns?.map((column: ColumnType) => {
						return (
							<Column
								key={column.id}
								column={column}
								perRow={perRow}
								position={`${factFigurePositions[factFigureAlign]}`}
								factStyle={factStyle}
								contentFontClass={contentFontClass}
								context={context}
								ctasStyle={ctasStyle}
							/>
						);
					})}
				</div>
			</div>
		</div>
	);
}

type FactFigureColumnType = {
	column: ColumnType;
	perRow: perRowsType;
	position: string;
	factStyle: string;
	contentFontClass?: FactFigureContentClass;
	context?: BlockKey;
	ctasStyle?: CtasObjectModel;
};

const Column = (props: FactFigureColumnType) => {
	const { column, perRow, position, factStyle, contentFontClass, context, ctasStyle } = props;

	const { id, title, link, fact, figure, description } = column;

	const propsColumnPerRow = {
		id: id,
		title: title,
		link: link,
		fact: fact,
		figure: figure,
		description: description,
		factFigureAlign: position,
		factStyle: factStyle,
		contentFontClass: contentFontClass,
		context: context,
		ctasStyle: ctasStyle,
	};

	const views = {
		1: <ColumnPerRow className='col-span-full flex flex-col' {...propsColumnPerRow} />,
		2: (
			<ColumnPerRow className='col-span-full flex flex-col tablet:col-span-6' {...propsColumnPerRow} />
		),
		3: (
			<ColumnPerRow className='col-span-full flex flex-col tablet:col-span-4' {...propsColumnPerRow} />
		),
		4: (
			<ColumnPerRow className='col-span-full flex flex-col tablet:col-span-3' {...propsColumnPerRow} />
		),
		6: (
			<ColumnPerRow className='col-span-full flex flex-col tablet:col-span-2' {...propsColumnPerRow} />
		),
	};

	return <>{views[perRow]}</>;
};

type FactFigureColumnPerRowType = {
	id: string;
	title: string;
	link?: ColumnLink;
	className: string | '';
	fact: string;
	figure: string;
	description: string;
	factFigureAlign: string;
	factStyle: string;
	contentFontClass?: FactFigureContentClass;
	context?: BlockKey;
	ctasStyle?: CtasObjectModel;
};

const ColumnPerRow = (props: FactFigureColumnPerRowType) => {
	const {
		id,
		title,
		link,
		className,
		fact,
		figure,
		description,
		factFigureAlign,
		factStyle,
		contentFontClass,
		context,
		ctasStyle,
	} = props;

	// Animation btn
	let animBtn;

	const animBtnPrimary = contentFontClass?.text?.btnPrimary?.animation;
	const animBtnSecondary = contentFontClass?.text?.btnSecondary?.animation;
	const animBtnTertiary = contentFontClass?.text?.btnTertiary?.animation;

	if (link?.style === 'primary') {
		animBtn = animBtnPrimary;
	} else if (link?.style === 'secondary') {
		animBtn = animBtnSecondary;
	} else if (link?.style === 'tertiary') {
		animBtn = animBtnTertiary;
	}

	const linkUrl: ColumnUrl | undefined = link?.url as ColumnUrl | undefined;

	return (
		<div id={id} className={`${className}`}>
			<div className={factFigureAlign}>
				{figure && (
					<h2
						className={`mb-3 last:mb-0 tablet:mb-3.5 tablet:last:mb-0 ${contentFontClass?.figureTitle}`}
					>
						{figure}
						{fact && (
							<div
								className={`my-3.5 mb-3 w-full last:mb-0 tablet:mb-3.5 tablet:last:mb-0 ${
									factStyle === 'framed' &&
									'rounded-minos-factFigure-fact-wrapper bg-minos-fact-wrapper-bg-color px-4 py-3.5'
								} ${contentFontClass?.factTitle}`}
							>
								{fact}
							</div>
						)}
					</h2>
				)}

				{title && <h3 className='minos-factFigure-inside-font-style'>{title}</h3>}
			</div>

			{description && (
				<div className={`mb-3 last:mb-0 tablet:mb-3.5 ${factFigureAlign}`}>
					<Html context={context} className={contentFontClass?.text} html={description} />
				</div>
			)}

			{/* Btn alignement according to position chosen of Fact&Figure */}
			{linkUrl?.url && (
				<div className={`mt-auto ${factFigureAlign}`}>
					<Button
						label={link?.label ? link?.label : ''}
						url={linkUrl.url}
						style={link?.style ? link?.style : 'primary'}
						urlType={linkUrl.type}
						urlOpenNewTab={linkUrl.opensInNewTab}
						btnHasIcon={link?.btnHasIcon}
						btnDecorationElement={link?.btnDecorationElement}
						animation={animBtn}
						btnClassName={getBtnSettingsToCSS(ctasStyle, link?.style ? link?.style : 'primary', context)}
					/>
				</div>
			)}
		</div>
	);
};
